import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, Loader2, Check } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { useOwned } from "@/lib/useOwned";
import { getRef } from "@/lib/affiliate";
import { IMG } from "@/lib/imagery";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Bundles() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const owned = useOwned();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/bundles").then((r) => setBundles(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const buy = async (b) => {
    if (!user) { toast.info("Sign in to purchase"); navigate("/login"); return; }
    setBuying(b.id);
    try {
      const { data } = await api.post("/orders", { bundle_id: b.id, provider: "pesapal", ref_code: getRef() || undefined });
      if (data.checkout_url && /^https?:\/\//.test(data.checkout_url)) window.location.href = data.checkout_url;
      else navigate(`/checkout/pay/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBuying(null); }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8 lg:py-20" data-testid="bundles-page">
      <Seo title="Bundles" description="Save more with HK Forexedge bundles — curated packs of EAs, indicators and courses at one discounted price." />
      <p className="overline mb-3">Bundles</p>
      <h1 className="font-editorial text-4xl font-semibold tracking-tight text-white sm:text-5xl">Buy more, save more.</h1>
      <p className="mt-4 max-w-2xl text-base text-emerald-100/70">Curated packs of tools and courses at one discounted price. Every item in a bundle is unlocked instantly on payment.</p>

      {loading ? (
        <p className="mt-16 text-center text-emerald-100/50">Loading…</p>
      ) : bundles.length === 0 ? (
        <div className="mt-10 card-edge p-10 text-center text-emerald-100/60">No bundles available right now.</div>
      ) : (
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {bundles.map((b) => (
            <div key={b.id} className="card-moss flex flex-col overflow-hidden" data-testid={`bundle-card-${b.id}`}>
              <div className="photo-vignette h-40"><img src={b.image_url || IMG.matrix} alt="" className="h-full w-full object-cover" /></div>
              <div className="flex flex-1 flex-col p-6">
                <div className="flex items-center gap-2"><Package size={16} className="text-lime-300" /><p className="font-editorial text-xl text-white">{b.title}</p></div>
                {b.subtitle && <p className="mt-1 text-sm text-emerald-100/60">{b.subtitle}</p>}
                <ul className="mt-4 grid flex-1 gap-1.5">
                  {b.members.map((m) => (
                    <li key={m.id} className="flex items-start gap-2 text-sm text-emerald-100/80">
                      <Check size={14} className="mt-0.5 text-lime-300" /> {m.title}
                      {owned.has(m.id) && <span className="chip chip-emerald ml-auto" data-testid={`bundle-owned-${m.id}`}>Owned</span>}
                    </li>
                  ))}
                </ul>
                {(() => {
                  const ownedMembers = b.members.filter((m) => owned.has(m.id));
                  return ownedMembers.length > 0 ? (
                    <p className="mt-2 text-[11px] text-lime-300/80" data-testid={`bundle-owned-note-${b.id}`}>
                      You already own {ownedMembers.map((m) => m.title).join(", ")} — you'll only be charged for the rest at checkout.
                    </p>
                  ) : null;
                })()}
                <div className="mt-5 flex items-end gap-2">
                  <span className="font-editorial text-3xl text-lime-300">{money(b.price)}</span>
                  {b.savings > 0 && <span className="mb-1 text-sm text-emerald-100/50 line-through">{money(b.sum_price)}</span>}
                  {b.savings > 0 && <span className="chip chip-emerald mb-1">Save {money(b.savings)}</span>}
                </div>
                <button onClick={() => buy(b)} disabled={buying === b.id} data-testid={`bundle-buy-${b.id}`} className="btn-edge mt-4 justify-center">
                  {buying === b.id ? <Loader2 size={16} className="animate-spin" /> : `Buy bundle · ${money(b.price)}`}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
